import {
  IProfile,
  isSamePassenger,
  ITripBookingPassenger,
  ITripRoom,
  ITripRoomGuest,
  transformGuestToPassenger,
  transformProfileToPassenger,
  TripRoomID,
  TripSearchResult,
} from "@cruisen/contract"
import { _t } from "@cruisen/i18n"
import { BookingRoom, ContactDetails } from "../types"

export const updateBookingRoomPassenger = ({
  bookingRooms,
  passengerData,
  guestRoomId,
}: {
  bookingRooms: BookingRoom[]
  passengerData: ITripBookingPassenger
  guestRoomId: string
}) =>
  bookingRooms.map((bookingRoom) => {
    if (bookingRoom.roomDetails.id !== guestRoomId) return bookingRoom

    if (bookingRoom.passengers === null) {
      return { ...bookingRoom, passengers: [passengerData] }
    }

    const doesPassengerExist = bookingRoom.passengers.some((passenger) =>
      isSamePassenger(passenger, passengerData),
    )

    if (!doesPassengerExist) {
      return { ...bookingRoom, passengers: [...bookingRoom.passengers, passengerData] }
    }

    return {
      ...bookingRoom,
      passengers: bookingRoom.passengers.map((passenger) =>
        isSamePassenger(passenger, passengerData) ? passengerData : passenger,
      ),
    }
  })

export type SelectionSummaryGuest = ITripRoomGuest & { roomId: TripRoomID }

export const getInitialStepData = ({
  bookingRooms,
  allTripRooms,
  cruiseDetails,
}: {
  bookingRooms: BookingRoom[]
  allTripRooms: ITripRoom[]
  cruiseDetails: TripSearchResult
}) => {
  const selectedRoomIds = bookingRooms.map((room) => room.roomDetails.id)
  const roomsForBooking = allTripRooms.filter((room) => selectedRoomIds.includes(room.id))
  const allGuests = roomsForBooking.reduce(
    (acc, room) => [...acc, ...room.guests.map((guest) => ({ ...guest, roomId: room.id }))],
    [] as SelectionSummaryGuest[],
  )

  const cruiseData = {
    image: cruiseDetails.heroImage,
    lineLogoImage: cruiseDetails.lineLogo,
    lineName: cruiseDetails.lineName,
    name: cruiseDetails.name,
    startDate: cruiseDetails.startDate,
    endDate: cruiseDetails.endDate,
    ports: cruiseDetails.ports,
  }

  return { allGuests, cruiseData }
}

export const transformRoomGuestsToPassengers = ({
  allGuests,
  profiles,
  bookingRooms,
  setPassengerData,
}: {
  allGuests: SelectionSummaryGuest[]
  profiles: IProfile[]
  bookingRooms: BookingRoom[]
  setPassengerData: (key: string, value: ITripBookingPassenger) => void
}) => {
  const roomsPassengers = new Map<TripRoomID, ITripBookingPassenger[]>()

  for (const guest of allGuests) {
    const guestProfile = profiles.find((profile) => guest.profileId === profile.id)

    const passengerData = !guestProfile
      ? transformGuestToPassenger(guest)
      : transformProfileToPassenger(guestProfile, guest)

    setPassengerData(guest.userId || guest.id, passengerData)

    let roomPassengers = roomsPassengers.get(guest.roomId)
    if (!Array.isArray(roomPassengers)) {
      roomPassengers = []
      roomsPassengers.set(guest.roomId, roomPassengers)
    }

    roomPassengers.push(passengerData)
  }

  return bookingRooms.map((bookingRoom) => {
    const passengers = roomsPassengers.get(bookingRoom.roomDetails.id) ?? null

    return {
      ...bookingRoom,
      passengers,
    } as BookingRoom
  })
}

export const getIsContactDetailsFilled = (loggedInUserProfile: ContactDetails | null) => {
  const requiredFields = [
    "address",
    "city",
    "country",
    "dob",
    "firstName",
    "lastName",
    "nationality",
    "postcode",
    "title",
  ]

  if (loggedInUserProfile === null) return false

  const allFieldsFilled = requiredFields.every(
    (field) => loggedInUserProfile[field as keyof ContactDetails],
  )
  const isUSCountyFilled = loggedInUserProfile.country !== "US" || loggedInUserProfile.county

  return Boolean(allFieldsFilled && isUSCountyFilled)
}
